import { useEffect, useState } from "react";
import { AppLayout } from "../components/Layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { MapView } from "../components/MapView";
import { useCountUp } from "../hooks/useCountUp";
import api from "../lib/api";
import { AlertTriangle, ClipboardList, Timer, TrendingUp } from "lucide-react";

function Kpi({ icon: Icon, label, value, suffix = "", accent }) {
  const animated = useCountUp(value || 0);
  return (
    <Card className="border-border" data-testid={`kpi-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4" style={{ color: accent }} strokeWidth={1.5} />
        </div>
        <p className="text-4xl font-black tracking-tighter">
          {animated}
          {suffix}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [riesgo, setRiesgo] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [barrios, setBarrios] = useState([]);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data));
    api.get("/dashboard/riesgo-sectores").then((r) => setRiesgo(r.data));
    api.get("/incidents").then((r) => setIncidents(r.data.filter((i) => i.lat && i.lon)));
    api.get("/gis/sectores").then((r) => setSectores(r.data));
    api.get("/gis/barrios").then((r) => setBarrios(r.data));
    api.get("/iris/tickets?limit=1500").then((r) => setTickets(r.data.filter((t) => t.lat && t.lon)));
  }, []);

  const heatmapPoints = tickets.map((t) => [t.lat, t.lon, 0.6]);

  // Colorea cada sector real del GIS según su índice de riesgo relativo (percentil sobre
  // el máximo actual), calculado a partir de /dashboard/riesgo-sectores.
  const riesgoPorSector = Object.fromEntries(riesgo.map((r) => [r.sector_id, r]));
  const maxIndice = Math.max(1, ...riesgo.map((r) => r.indice_riesgo));
  const sectoresConRiesgo = sectores.map((s) => {
    const r = riesgoPorSector[s._id];
    if (!r || r.indice_riesgo === 0) return { ...s, nivel_riesgo: "sin_datos", indice_riesgo: 0 };
    const pct = r.indice_riesgo / maxIndice;
    const nivel_riesgo = pct >= 0.66 ? "alto" : pct >= 0.33 ? "medio" : "bajo";
    return { ...s, nivel_riesgo, indice_riesgo: r.indice_riesgo };
  });

  return (
    <AppLayout title="Cuadro de mando ejecutivo">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi icon={ClipboardList} label="Fichas IRIS (ámbito)" value={summary?.total_fichas_iris} accent="#0A84FF" />
        <Kpi icon={TrendingUp} label="Fichas últimos 90 días" value={summary?.fichas_iris_90d} accent="#7AB800" />
        <Kpi icon={AlertTriangle} label="Incidencias abiertas" value={summary?.incidencias_abiertas} accent="#FF9F0A" />
        <Kpi icon={Timer} label="SLA en riesgo" value={summary?.incidencias_sla_riesgo} accent="#FF3B30" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest">Mapa operativo — heatmap IRIS + incidencias + sectores</CardTitle>
            </CardHeader>
            <CardContent>
              <MapView heatmapPoints={heatmapPoints} incidents={incidents} sectores={sectoresConRiesgo} barrios={barrios} height="520px" />
            </CardContent>
          </Card>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">Índice de riesgo por sector</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="risk-index-list">
            {riesgo.map((s) => (
              <div key={s.sector_id} className="flex items-center justify-between border-b border-border pb-2 last:border-0">
                <div>
                  <p className="text-sm font-bold">{s.nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.quejas_iris} quejas · {s.incidencias_abiertas} abiertas
                  </p>
                </div>
                <span className="text-lg font-black text-accent-warning">{s.indice_riesgo}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">Top temáticas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {summary?.top_tematicas?.map((t, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-border py-1.5 last:border-0">
                <span>{t._id}</span>
                <span className="font-bold">{t.total}</span>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-widest">Top barrios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {summary?.top_barrios?.map((t, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-border py-1.5 last:border-0">
                <span>{t._id}</span>
                <span className="font-bold">{t.total}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
